import "core-js/modules/es.array.push.js";
import { defineComponent, ref, watch, computed } from 'vue';
import { cloneDeep, kebabCase } from 'lodash';
import { useFilters } from '@/use/filters';
import { useInfiniteScroll } from '@vueuse/core';
import { DRAWER_CLOSED, DRAWER_OPENED, GENERIC_EMPTY_STATE } from '@/constants/icons';
import { ExpandTypeState } from '@/types/enums/expand-type';
export default defineComponent({
  props: {
    data: {
      type: Array,
      default: () => []
    },
    columns: {
      type: Object,
      default: () => {}
    },
    currentPage: {
      type: Number,
      required: true
    },
    totalPages: {
      type: Number,
      required: true
    },
    nextPage: {
      type: Number,
      required: true
    },
    selectedResources: {
      type: Array,
      default: () => []
    },
    selectedId: {
      type: Number,
      default: -1
    },
    listeners: {
      type: Array,
      default: () => []
    },
    hideHeader: {
      type: Boolean,
      default: false
    },
    allowCards: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      required: true
    },
    additionalData: {
      type: Object,
      default: () => {}
    },
    currentExpandState: {
      type: String,
      default: ExpandTypeState.WORK_ORDER_LEVEL
    },
    isNestedTable: {
      type: Boolean,
      default: false
    },
    isParentSelected: {
      type: Boolean,
      default: false
    },
    isBulkSelectAll: {
      type: Boolean,
      default: false
    }
  },
  setup: (props, {
    emit
  }) => {
    const localSelectedResources = ref(cloneDeep(props.selectedResources));
    const isLocalBulkSelectAll = ref(props.isBulkSelectAll);
    watch(() => props.selectedResources, () => {
      localSelectedResources.value = cloneDeep(props.selectedResources);
    });
    watch(() => props.isBulkSelectAll, () => {
      isLocalBulkSelectAll.value = props.isBulkSelectAll;
    });
    const init = () => {
      return props.listeners.reduce((acc, text) => {
        acc[kebabCase(text)] = event => {
          emit(`${kebabCase(text)}`, event);
        };
        return acc;
      }, {});
    };
    const listenerObj = ref(init());
    const selectDeselectResource = resource => {
      resource.selected = !resource.selected;
      const index = localSelectedResources.value.findIndex(res => res.id === resource.id);
      if (index !== -1) {
        localSelectedResources.value.splice(index, 1);
      } else {
        localSelectedResources.value.push(resource);
      }
    };
    const emitSelectedResources = () => {
      setTimeout(() => {
        emit('update:selected-resources', localSelectedResources.value);
      });
    };
    const {
      currentRansack
    } = useFilters();
    const dataIteratorRef = ref(null);
    const previousDataCount = ref(props.data.length);
    const hasNextPage = computed(() => {
      return props.data.length && (props.nextPage || props.currentPage < props.totalPages && props.totalPages !== -1);
    });
    const {
      reset
    } = useInfiniteScroll(dataIteratorRef, () => {
      loadMore();
    }, {
      canLoadMore: () => hasNextPage.value || props.totalPages === -1
    });
    const loadMore = () => {
      /*
        if i have a next page of data and
        my locally stored count of rows does != recently passed data length
        load more data
        if they are the same that means I am still waiting for more data
        and I shouldnt fire load more until I get my next page of data
      */
      if (hasNextPage.value && (previousDataCount.value !== props.data.length || props.previousDataCount === 0)) {
        previousDataCount.value = props.data.length;
        emit('load-more');
      }
    };
    watch(() => props.data, () => {
      if (!props.data.length) {
        reset();
        previousDataCount.value = props.data.length;
      }
    });
    const itemSelected = event => {
      const item = event.item; // Extract the selected item
      const value = !!localSelectedResources.value.some(r => r.id === item.id);
      emit('item-selected', {
        item,
        value
      });
    };
    const localCurrentExpandState = computed(() => props.currentExpandState);
    const nestedItemSelected = (isSelected, nestedItem) => {
      // TODO: Do we need to restrict for non nested rows? (Lets talk)
      emit('nested-item-selected', isSelected, nestedItem);
    };
    return {
      listenerObj,
      localSelectedResources,
      emitSelectedResources,
      currentRansack,
      dataIteratorRef,
      hasNextPage,
      GENERIC_EMPTY_STATE,
      DRAWER_CLOSED,
      DRAWER_OPENED,
      selectDeselectResource,
      itemSelected,
      localCurrentExpandState,
      nestedItemSelected,
      isLocalBulkSelectAll
    };
  }
});